import React, { useCallback } from 'react';
import classnames from 'classnames';
import ReactModal from 'react-modal';
import SvgComponent from 'components/Form/SvgComponent';
import { ModalContext } from './ModalContext';
import { ModalButton } from './Button';

interface Props {
  className?: string
  /**
   * Shows the dialog.
   *
   * Default: true
   */
  show?: boolean
  /**
   * When set to true, this will add additional close buttons to the modal.
   *
   * Default: false
   */
  closeable?: boolean
  /**
   * When closeable is set to true and the user clicks the buttons associated to
   * close, the provided function will trigger.,
   */
  onDismiss?: () => void
}

/**
 * Modal
 *
 * ### Known Side Effects:
 *
 * 1. Many ReactModalPortal divs. This is a known issue with React-Modal and
 *    related to their compatiability with React 15.
 * 2. When modals are not closed properly (modal-open stays applied to body),
 *    it'll stop the page from scrolling and has an impact on "tab". Please see
 *    the troubleshooting below.
 *
 * ### Troubleshooting:
 *
 * #### modal-open
 *
 * In the scenario 'modal-open' is left on the page after modals are closed,
 * this is due to a modal unmounting before it was closed. To mitigate against
 * this, you'll need to close it then unmount it. Majority of the time, the
 * parent component is conditionally rendering the modal, versus letting the
 * modal handle the new state. Other modals may have impact on existing modals
 * as well.
 *
 * If the known side effects become a large issue overall, we may need to do the
 * following:
 * 1. Rework all the "close" buttons to use ModalCloseButton. Thus, when
 *    closeModal gets called by each modal. When something isn't closed
 *    properly, we'll need to update closeModal detect these instances and clean
 *    up.
 * 2. Migrate away from React-Modal and use our own implementation. The downside
 *    is that we'll need to address the accessibility ourselves.
 */
const Modal: React.FC<Props> = (props) => {
  const {
    show = true, closeable = false, children, className, onDismiss
  } = props;
  const [isOpen, setIsOpen] = React.useState(show);

  React.useEffect(() => {
    if (show !== undefined) {
      setIsOpen(show);
    }
  }, [props]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    onDismiss?.(); // eslint-disable-line no-unused-expressions
  }, []);

  const ref = React.createRef<ReactModal>();

  return (
    <ModalContext.Provider value={{ onClose: closeModal }}>
      <ReactModal
        bodyOpenClassName="modal-open"
        ariaHideApp
        ref={ref}
        isOpen={isOpen}
        className={classnames(className, 'modal')}
        overlayClassName="overlay"
      >
        <div className="modal-container">
          {closeable && (
            <ModalButton
              className="modal-container-close-icon"
              title="Close Dialog"
              onClick={closeModal}
            >
              <SvgComponent src="/img/close-icon.svg" />
            </ModalButton>
          )}

          <div className="modal-content">
            {children}
            {closeable && <ModalButton aria-label="Close Dialog">Close</ModalButton>}
          </div>
        </div>
      </ReactModal>
    </ModalContext.Provider>
  );
};

// https://github.com/reactjs/react-modal/issues/576
if (process.env.JEST_WORKER_ID !== undefined) {
  ReactModal.setAppElement('body');
} else {
  ReactModal.setAppElement('#app');
}

export default Modal;
